import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/api/client";
import { ProviderGlyph } from "@/components/ProviderGlyph";
import { useToast } from "@/components/Toast";

type Proposal = {
  var_name: string;
  provider: string;
  upstream_base_url: string;
  auth_scheme: string;
  auth_header_name: string | null;
  auth_username: string | null;
  value_preview: string;
  default_label: string;
};

type Preview = {
  staging_token: string | null;
  expires_at?: string;
  proposals: Proposal[];
  ignored: string[];
  unrecognized: string[];
};

type ConfirmResult = {
  created: { id: number; label: string; provider: string }[];
  failed: { label: string; error: string }[];
};

export function Import() {
  const [text, setText] = useState("");
  const [project, setProject] = useState("");
  const [preview, setPreview] = useState<Preview | null>(null);
  const [result, setResult] = useState<ConfirmResult | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [labels, setLabels] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const toast = useToast();
  const nav = useNavigate();

  async function doPreview(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setResult(null);
    try {
      const p = await api<Preview>("/api/admin/import/preview", {
        method: "POST",
        json: { env_text: text, project: project || null },
      });
      setPreview(p);
      const all = new Set(p.proposals.map((x) => x.var_name));
      setSelected(all);
      const ls: Record<string, string> = {};
      p.proposals.forEach((x) => {
        ls[x.var_name] = x.default_label;
      });
      setLabels(ls);
    } catch (e: any) {
      toast.error(e?.message ?? "parse failed");
    } finally {
      setLoading(false);
    }
  }

  async function confirm() {
    if (!preview) return;
    if (!preview.staging_token) {
      toast.error("staging token expired — please re-preview");
      return;
    }
    const items = preview.proposals
      .filter((p) => selected.has(p.var_name))
      .map((p) => ({
        var_name: p.var_name,
        label: labels[p.var_name] || p.default_label,
      }));
    if (items.length === 0) {
      toast.error("No items selected");
      return;
    }
    if (
      !confirm_native(
        `Import ${items.length} real key${items.length === 1 ? "" : "s"}? The pasted values will be encrypted at rest.`
      )
    )
      return;
    setLoading(true);
    try {
      const r = await api<ConfirmResult>("/api/admin/import/confirm", {
        method: "POST",
        json: { token: preview.staging_token, items },
      });
      setResult(r);
      if (r.failed.length === 0) toast.success(`Imported ${r.created.length} keys`);
      else toast.error(`${r.created.length} imported, ${r.failed.length} failed`);
    } catch (e: any) {
      toast.error(e?.message ?? "import failed");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="space-y-6 max-w-4xl">
      <header>
        <h1 className="text-3xl display">Import keys</h1>
        <p className="text-sm text-slate-400">
          Paste a project's <code>.env</code> contents. Gatekeeper classifies each variable
          against known providers (Anthropic, OpenAI, Finnhub, Twilio, …), hides secrets
          behind a masked preview, and lets you pick which ones to commit. Nothing is saved
          until you click <b>Import</b>.
        </p>
      </header>

      {!preview && (
        <form onSubmit={doPreview} className="card space-y-4">
          <label className="block">
            <div className="text-xs text-slate-400 mb-1">Project hint (optional)</div>
            <input
              className="input"
              value={project}
              onChange={(e) => setProject(e.target.value)}
              placeholder="avon, adp-hr, sam…"
              aria-label="Project hint for labels"
            />
            <p className="text-[11px] text-slate-500 mt-1">
              Used in the suggested label: e.g. "anthropic-avon". You can edit each label in the preview step.
            </p>
          </label>
          <label className="block">
            <div className="text-xs text-slate-400 mb-1">Paste .env contents</div>
            <textarea
              className="input mono text-xs"
              rows={14}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={`# paste here\nANTHROPIC_API_KEY=sk-ant-...\nOPENAI_API_KEY=sk-...\n`}
              required
            />
            <p className="text-[11px] text-slate-500 mt-1">
              Sent to the server once over HTTPS, encrypted immediately, held in a 10-minute
              staging table, then moved to real_keys when you confirm. The plaintext never
              round-trips back to the browser.
            </p>
          </label>
          <div className="flex justify-end">
            <button className="btn-primary" disabled={loading || !text.trim()} type="submit">
              {loading ? "Parsing…" : "Preview"}
            </button>
          </div>
        </form>
      )}

      {preview && !result && (
        <>
          <div className="card">
            <h2 className="font-semibold mb-3">
              Proposed imports ({preview.proposals.length})
            </h2>
            {preview.proposals.length === 0 ? (
              <p className="text-sm text-slate-500">
                No recognized provider keys in that paste. Unrecognized vars:{" "}
                {preview.unrecognized.join(", ") || "none"}
              </p>
            ) : (
              <ul className="divide-y divide-white/5">
                {preview.proposals.map((p) => {
                  const checked = selected.has(p.var_name);
                  return (
                    <li
                      key={p.var_name}
                      className="py-3 flex items-start gap-3"
                    >
                      <input
                        type="checkbox"
                        className="mt-2 accent-accent-500"
                        checked={checked}
                        onChange={(e) => {
                          const next = new Set(selected);
                          if (e.target.checked) next.add(p.var_name);
                          else next.delete(p.var_name);
                          setSelected(next);
                        }}
                      />
                      <ProviderGlyph provider={p.provider} size={32} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <input
                            className="inline-edit text-sm font-medium bg-transparent text-slate-100"
                            value={labels[p.var_name] ?? p.default_label}
                            onChange={(e) =>
                              setLabels({ ...labels, [p.var_name]: e.target.value })
                            }
                          />
                          <span className="text-xs text-slate-500">· {p.provider}</span>
                        </div>
                        <div className="mono text-[11px] text-slate-500 mt-0.5 truncate">
                          {p.var_name} → {p.upstream_base_url} ·{" "}
                          <span>{p.auth_scheme}</span>
                          {p.auth_header_name ? ` · ${p.auth_header_name}` : ""}
                        </div>
                        <div className="mono text-[11px] text-slate-400 mt-1">
                          value: {p.value_preview}
                          {p.auth_username ? ` · user: ${p.auth_username}` : ""}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {preview.unrecognized.length > 0 && (
            <div className="card bg-amber-500/5 border-amber-500/20">
              <h2 className="font-semibold text-sm mb-2 text-amber-200">
                Unrecognized secrets ({preview.unrecognized.length})
              </h2>
              <p className="text-xs text-slate-400 mb-2">
                These look like secrets but don't match any known provider rule. Add a rule to
                <code> backend/app/services/import_service.py</code> and re-try, or add them
                manually via <b>Add real key</b>.
              </p>
              <div className="mono text-[11px] text-slate-400 flex flex-wrap gap-x-3 gap-y-1">
                {preview.unrecognized.map((v) => (
                  <span key={v}>{v}</span>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-between items-center">
            <button
              className="btn-secondary"
              onClick={() => {
                setPreview(null);
                setSelected(new Set());
              }}
            >
              ← Back
            </button>
            <button
              className="btn-primary"
              disabled={loading || selected.size === 0}
              onClick={confirm}
            >
              {loading
                ? "Importing…"
                : `Import ${selected.size} key${selected.size === 1 ? "" : "s"}`}
            </button>
          </div>
        </>
      )}

      {result && (
        <div className="card">
          <h2 className="font-semibold mb-3">Import results</h2>
          {result.created.length > 0 && (
            <>
              <h3 className="text-sm text-emerald-300 mb-1">
                ✓ Created ({result.created.length})
              </h3>
              <ul className="text-sm text-slate-300 space-y-0.5 mb-4">
                {result.created.map((c) => (
                  <li key={c.id}>
                    #{c.id} {c.label} ({c.provider})
                  </li>
                ))}
              </ul>
            </>
          )}
          {result.failed.length > 0 && (
            <>
              <h3 className="text-sm text-rose-300 mb-1">✗ Failed ({result.failed.length})</h3>
              <ul className="text-sm text-slate-400 space-y-0.5">
                {result.failed.map((f) => (
                  <li key={f.label}>
                    {f.label}: {f.error}
                  </li>
                ))}
              </ul>
            </>
          )}
          <div className="mt-4 flex gap-2">
            <button
              className="btn-secondary"
              onClick={() => {
                setPreview(null);
                setResult(null);
                setText("");
                setSelected(new Set());
              }}
            >
              Import more
            </button>
            <button className="btn-primary" onClick={() => nav("/real-keys")}>
              Go to Real keys
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function confirm_native(msg: string): boolean {
  return window.confirm(msg);
}
